import { fetchWithAuth } from 'utils/fetchWithAuth';
import { unwrapResponse as unwrap } from 'utils/unwrapResponse';

/** Mirrors `AUDIT_ACTIONS` in `backend/types/audit.ts` (J1's CHECK constraint). */
export const AUDIT_ACTIONS = [
  'create',
  'update',
  'exclude',
  'reinstate',
  'note',
  'import',
  'cancel',
  'remind',
  'accept',
  'decline',
  'deactivate',
  'reactivate',
  'delete',
  'grant',
  'realign',
  'resend_invitation',
] as const;
export type AuditAction = (typeof AUDIT_ACTIONS)[number];

/** One field's change (J3). `masked` never carries a value (J5) — only that the field changed. */
export type AuditChange = { from?: unknown; to: unknown } | { masked: true };
export type AuditChanges = Record<string, AuditChange>;

export const isMaskedChange = (change: AuditChange): change is { masked: true } =>
  'masked' in change && change.masked === true;

export interface AuditEventActor {
  id: number;
  name: string;
  role?: string | null;
}

/** One event as `GET /api/admin/audit-events` returns it (J11). */
export interface AdminAuditEvent {
  id: string;
  occurredAt: string;
  actor: AuditEventActor | null;
  module: string;
  entityType: string;
  entityId: string;
  entityLabel: string | null;
  action: AuditAction;
  changes: AuditChanges;
  context: Record<string, unknown>;
}

export interface AdminAuditEventsFilters {
  entityType?: string;
  entityId?: string;
  importId?: string;
  realignmentId?: string;
  actorId?: number;
  module?: string;
  action?: AuditAction;
  source?: 'system';
  from?: string;
  to?: string;
  q?: string;
  cursor?: string;
}

export interface AdminAuditEventsPage {
  events: AdminAuditEvent[];
  nextCursor: string | null;
  total: number;
  since: string | null;
  retentionMonths: number;
}

const BASE = 'admin/audit-events';

/** `entityType`/`entityId` must travel together (backend refines on it) — every other filter is optional. */
const auditEventsQuery = (filters: AdminAuditEventsFilters): string => {
  const params = new URLSearchParams();
  const set = (key: keyof AdminAuditEventsFilters) => {
    const value = filters[key];
    if (value !== undefined && value !== '') params.set(key, String(value));
  };
  (['entityType', 'entityId', 'importId', 'realignmentId', 'actorId', 'module', 'action', 'source', 'from', 'to', 'q', 'cursor'] as const).forEach(set);
  return params.toString();
};

/** A 403 (permission revoked mid-session, CA24) surfaces as `ApiError.status` for the caller to branch on. */
export const fetchAuditEvents = async (filters: AdminAuditEventsFilters): Promise<AdminAuditEventsPage> =>
  unwrap<AdminAuditEventsPage>(await fetchWithAuth(`${BASE}?${auditEventsQuery(filters)}`), 'Failed to fetch the audit events');
